import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    User, DollarSign, CreditCard, AlertTriangle, Bell,
    CheckCircle, Eye, Download, Printer, Banknote,
    ClipboardList, BarChart, Clock, Shield, Search,
    FileText, ArrowLeft, ArrowRight, LayoutDashboard, UserCheck, XCircle,
    Mail, GraduationCap, School, Calendar, Info
} from 'lucide-react';
import api from '../api';
import Loader from '../components/Loader';
import './ComplianceDashboard.css';
import CardInfoTooltip from '../components/CardInfoTooltip';

function ComplianceDashboard() {
    // Navigation state
    const [activeTab, setActiveTab] = useState('dashboard');


    // Data state
    const [kycQueue, setKycQueue] = useState([]);
    const [pendingKYC, setPendingKYC] = useState([]);
    const [approvedKYC, setApprovedKYC] = useState([]);

    const [escrowTransactions, setEscrowTransactions] = useState([]);
    const [paymentStats, setPaymentStats] = useState(null);
    const [reportsData, setReportsData] = useState(null);
    const [conflicts, setConflicts] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [pendingPayments, setPendingPayments] = useState([]);
    const [refundQueue, setRefundQueue] = useState([]);

    // Document viewer state
    const [showDocViewer, setShowDocViewer] = useState(false);
    const [selectedDocuments, setSelectedDocuments] = useState(null);

    // KYC Verification Modal
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [currentApplicant, setCurrentApplicant] = useState(null);
    const [verificationNotes, setVerificationNotes] = useState('');

    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [toast, setToast] = useState(null);
    const [rejectionNotes, setRejectionNotes] = useState('');
    const navigate = useNavigate();

    // Confirmation modal state
    const [confirmModal, setConfirmModal] = useState({ show: false, type: null, data: null });

    // Polling interval for real-time sync
    const SYNC_INTERVAL = 60000; // 60 seconds

    const fetchKYCData = useCallback(async (silent = false) => {
        try {
            const kycRes = await api.get('/compliance/kyc-applicants');
            setKycQueue(kycRes.data);

            // Separate lists - exclude only REJECTED from KYC queue
            setPendingKYC(kycRes.data.filter(item => item.status !== 'REJECTED'));
            setApprovedKYC(kycRes.data.filter(item => item.status === 'ACCEPTED'));
        } catch (err) {
            if (!silent) {
                console.log('Using mock KYC data');
                setKycQueue([
                    {
                        id: "PoP-882",
                        user_id: 882,
                        name: "Dr. James Wilson",
                        email: "james.wilson@example.com",
                        domain: "Fintech",
                        risk: "Low",
                        status: "UNDER_REVIEW",
                        docs: ["Govt ID", "PhD Cert", "Exp Letter"],
                        documents: [
                            { type: "Government ID", url: "https://via.placeholder.com/600x400/3b82f6/ffffff?text=Government+ID", uploaded: "2024-12-20" },
                            { type: "PhD Certificate", url: "https://via.placeholder.com/600x400/10b981/ffffff?text=PhD+Certificate", uploaded: "2024-12-20" },
                            { type: "Experience Letter", url: "https://via.placeholder.com/600x400/f59e0b/ffffff?text=Experience+Letter", uploaded: "2024-12-20" }
                        ],
                        uploaded_at: new Date(Date.now() - 86400000).toISOString()
                    },
                    {
                        id: "PoP-883",
                        user_id: 883,
                        name: "Prof. Emily Zhang",
                        email: "emily.zhang@example.com",
                        domain: "AI/ML",
                        risk: "High",
                        status: "PENDING",
                        docs: ["Govt ID", "Research Papers"],
                        documents: [
                            { type: "Government ID", url: "https://via.placeholder.com/600x400/ef4444/ffffff?text=ID+Verification+Failed", uploaded: "2024-12-19" },
                            { type: "Research Papers", url: "https://via.placeholder.com/600x400/8b5cf6/ffffff?text=Research+Publications", uploaded: "2024-12-19" }
                        ],
                        uploaded_at: new Date(Date.now() - 172800000).toISOString()
                    }
                ]);
            }
        }
    }, []);

    const fetchDashboardData = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            await fetchKYCData(true);

            const results = await Promise.allSettled([
                api.get('/compliance/escrows'),
                api.get('/compliance/stats'),
                api.get('/compliance/reports/analytics'),
                api.get('/compliance/notifications'),
                api.get('/compliance/conflicts'),
                api.get('/payment/compliance/pending-payments'),
                api.get('/refunds/admin/queue')
            ]);

            if (results[0].status === 'fulfilled') setEscrowTransactions(results[0].value.data);
            else console.error('Escrow fetch failed', results[0].reason);

            if (results[1].status === 'fulfilled') setPaymentStats(results[1].value.data);
            else {
                console.log('Using mock stats');
                setPaymentStats({
                    escrow_total: 420000,
                    pending_kyc: 5,
                    released_payouts: 12,
                    total_transactions: 15
                });
            }

            if (results[2].status === 'fulfilled') setReportsData(results[2].value.data);
            if (results[3].status === 'fulfilled') setNotifications(results[3].value.data);

            if (results[4].status === 'fulfilled') setConflicts(results[4].value.data);
            if (results[5] && results[5].status === 'fulfilled') setPendingPayments(results[5].value.data);
            if (results[6] && results[6].status === 'fulfilled') setRefundQueue(results[6].value.data);
            else {
                // Mock fallback
                setConflicts([
                    {
                        id: 1,
                        type: "Institutional",
                        professor: "Dr. James Wilson",
                        university: "IIT Delhi",
                        severity: "High",
                        status: "Pending",
                        description: "Professor is currently employed at IIT Delhi",
                        documents: [
                            { type: "Employment Letter", url: "https://via.placeholder.com/600x400/ef4444/ffffff?text=IIT+Delhi+Employment+Letter", uploaded: "2024-12-20" },
                            { type: "Conflict Declaration", url: "https://via.placeholder.com/600x800/f59e0b/ffffff?text=Conflict+of+Interest+Declaration", uploaded: "2024-12-20" }
                        ]
                    }
                ]);
            }

            // Mock audit logs
            setAuditLogs([
                { id: 1, action: "ESCROW_RELEASED", user: "Admin", timestamp: new Date().toISOString(), details: "Transaction #1 released" },
                { id: 2, action: "KYC_APPROVED", user: "Compliance Officer", timestamp: new Date(Date.now() - 3600000).toISOString(), details: "PoP-881 approved" },
                { id: 3, action: "PAYMENT_CAPTURED", user: "System", timestamp: new Date(Date.now() - 7200000).toISOString(), details: "Payment ₹500 captured" }
            ]);

        } catch (err) {
            console.error('Error fetching data:', err);
            if (typeof showToast === 'function') showToast('Error loading dashboard data', 'error');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [fetchKYCData]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchDashboardData(true);
        setIsRefreshing(false);
    };

    useEffect(() => {
        fetchDashboardData();

        // Set up polling for real-time updates
        const pollingInterval = setInterval(() => {
            fetchDashboardData(true); // Refreshes everything including escrows and notifications
        }, SYNC_INTERVAL);

        // Set up visibility change listener
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchDashboardData(true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(pollingInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchDashboardData, fetchKYCData]);

    // Update lists when kycQueue changes (fallback if strictly local updates happen)
    useEffect(() => {
        if (kycQueue.length > 0) {
            // Exclude only REJECTED from KYC queue (keep ACCEPTED so docs can be viewed)
            setPendingKYC(kycQueue.filter(item => item.status !== 'REJECTED'));
            setApprovedKYC(kycQueue.filter(item => item.status === 'ACCEPTED'));
        }
    }, [kycQueue]);


    // View Documents Handler - Shows document selector modal
    const handleViewDocuments = async (item, type) => {
        if (type === 'kyc') {
            // Fetch KYC documents and show in modal
            try {
                const response = await api.get(`/compliance/kyc-documents/${item.user_id}`);

                if (!response.data.documents || response.data.documents.length === 0) {
                    showToast('No documents found for this applicant', 'warning');
                    return;
                }

                // Prepare documents for the viewer
                const documents = response.data.documents.map(doc => ({
                    name: doc.name,
                    type: doc.type,
                    user_id: item.user_id
                }));

                setSelectedDocuments({
                    type: 'kyc',
                    title: `KYC Documents - ${item.name}`,
                    subtitle: `ID: ${item.id} | Email: ${item.email} | Domain: ${item.domain}`,
                    documents: documents,
                    metadata: item
                });
                setShowDocViewer(true);
            } catch (err) {
                console.error('Error fetching KYC documents:', err);
                showToast('Failed to load KYC documents', 'error');
            }
        } else {
            // For other document types, keep the existing viewer
            setSelectedDocuments({
                type: type,
                title: type === 'escrow' ? `Session Documents - ${item.session_title}` :
                    type === 'conflict' ? `Conflict Documents - ${item.professor}` : 'Documents',
                subtitle: type === 'escrow' ? `Transaction #${item.id} | Amount: ₹${item.amount}` :
                    type === 'conflict' ? `${item.type} Conflict | Severity: ${item.severity}` : '',
                documents: item.documents || [],
                metadata: item
            });
            setShowDocViewer(true);
        }
    };


    // Open Verification Modal
    const handleOpenVerification = (applicant) => {
        setCurrentApplicant(applicant);
        setShowVerificationModal(true);
    };

    // Handle KYC Verification Action
    const handleKYCAction = async (action) => {
        if (!currentApplicant) return;

        const actionText = {
            'ACCEPTED': 'approve',
            'REJECTED': 'reject',
            'PENDING': 'mark as pending',
            'UNDER_REVIEW': 'move to under review'
        }[action];

        setConfirmModal({
            show: true,
            type: 'kyc',
            data: { applicant: currentApplicant, action, actionText, notes: verificationNotes }
        });
    };

    const confirmKYCAction = async () => {
        const { applicant, action, actionText, notes } = confirmModal.data;
        setConfirmModal({ show: false, type: null, data: null });

        try {
            await api.post('/compliance/kyc/verify', {
                applicant_id: applicant.user_id,
                action: action,
                notes: notes || null
            });

            // Update local state
            setKycQueue(prev => prev.map(item =>
                item.user_id === applicant.user_id
                    ? { ...item, status: action, risk: action === 'ACCEPTED' ? 'Low' : action === 'REJECTED' ? 'High' : 'Medium' }
                    : item
            ));

            // Add to audit log
            const newLog = {
                id: auditLogs.length + 1,
                action: `KYC_${action}`,
                user: "Compliance Officer",
                timestamp: new Date().toISOString(),
                details: `${applicant.name} (${applicant.id})`
            };
            setAuditLogs(prev => [newLog, ...prev]);

            showToast(`KYC ${actionText}d successfully for ${applicant.name}`, 'success');
            setShowVerificationModal(false);
            setCurrentApplicant(null);
            setVerificationNotes('');

        } catch (err) {
            console.error('Verification error:', err);
            showToast(`Failed to ${actionText} KYC`, 'error');
        }
    };

    const handleConfirmEscrow = (transaction) => {
        setConfirmModal({
            show: true,
            type: 'escrow',
            data: transaction
        });
    };

    const confirmEscrowPayment = async () => {
        const transaction = confirmModal.data;
        setConfirmModal({ show: false, type: null, data: null });

        try {
            await api.post(`/payment/compliance/confirm/${transaction.id}`);

            // Update local states
            setEscrowTransactions(prev => prev.map(t =>
                t.id === transaction.id ? { ...t, status: 'ESCROWED' } : t
            ));

            // Remove from pending payments list if present
            setPendingPayments(prev => prev.filter(p => p.id !== transaction.id));

            showToast('Payment confirmed and escrowed', 'success');
        } catch (err) {
            console.error('Confirmation error:', err);
            showToast('Failed to confirm payment', 'error');
        }
    };

    const handleMarkComplete = (transaction) => {
        setConfirmModal({
            show: true,
            type: 'complete',
            data: transaction
        });
    };

    const confirmMarkComplete = async () => {
        const transaction = confirmModal.data;
        setConfirmModal({ show: false, type: null, data: null });

        try {
            // Using session_id and session_type from the transaction record
            await api.post(`/compliance/sessions/${transaction.session_id}/complete?session_type=${transaction.session_type}`);
            setEscrowTransactions(prev => prev.map(t =>
                t.id === transaction.id ? { ...t, is_session_completed: true, session_status: 'COMPLETED' } : t
            ));
            showToast('Session marked as completed', 'success');
        } catch (err) {
            showToast(`Failed: ${err.response?.data?.detail || err.message}`, 'error');
        }
    };

    const handleReleaseEscrow = (transaction) => {
        setConfirmModal({
            show: true,
            type: 'release',
            data: transaction
        });
    };

    const confirmReleaseEscrow = async () => {
        const transaction = confirmModal.data;
        setConfirmModal({ show: false, type: null, data: null });

        try {
            await api.post(`/compliance/escrows/${transaction.id}/release`);

            setEscrowTransactions(prev =>
                prev.map(t => t.id === transaction.id ? { ...t, status: 'RELEASED' } : t)
            );

            showToast(`₹${transaction.amount} released to ${transaction.professor_name}`, 'success');
        } catch (err) {
            showToast('Failed to release escrow', 'error');
        }
    };

    const handleResolveConflict = (conflictId, action) => {
        setConfirmModal({
            show: true,
            type: 'conflict',
            data: { conflictId, action }
        });
    };

    const confirmResolveConflict = async () => {
        const { conflictId, action } = confirmModal.data;
        setConfirmModal({ show: false, type: null, data: null });

        try {
            await api.post(`/compliance/resolve-conflict?conflict_id=${conflictId}&action=${action}`);
            setConflicts(prev => prev.map(c =>
                c.id === conflictId ? { ...c, status: 'Resolved', resolution: action } : c
            ));
            showToast(`Conflict resolved via ${action}`, 'success');
        } catch (err) {
            showToast('Failed to resolve conflict', 'error');
        }
    };

    const handleRefundRequest = (request, action) => {
        setConfirmModal({
            show: true,
            type: 'refund_request',
            data: { request, action }
        });
    };

    const confirmRefundRequest = async () => {
        const { request, action } = confirmModal.data;
        setConfirmModal({ show: false, type: null, data: null });

        try {
            const payload = action === 'REJECTED' ? { rejection_reason: rejectionNotes } : {};
            await api.post(`/refunds/admin/${request.id}/process?action=${action}`, payload);
            setRefundQueue(prev => prev.filter(r => r.id !== request.id));
            if (action === 'APPROVED') {
                setEscrowTransactions(prev => prev.map(t =>
                    t.payment_id === request.payment_id ? { ...t, status: 'REFUNDED' } : t
                ));
            }
            showToast(`Refund request ${action.toLowerCase()} successfully`, 'success');
            setRejectionNotes('');
        } catch (err) {
            showToast('Failed to process refund request', 'error');
        }
    };

    const handleDirectRefundEscrow = (escrow) => {
        setConfirmModal({
            show: true,
            type: 'direct_refund',
            data: escrow
        });
    };

    const confirmDirectRefund = async () => {
        const escrow = confirmModal.data;
        setConfirmModal({ show: false, type: null, data: null });

        try {
            await api.post(`/refunds/admin/direct-refund-escrow/${escrow.id}?reason=Compliance Intervention`);
            setEscrowTransactions(prev => prev.map(t =>
                t.id === escrow.id ? { ...t, status: 'REFUNDED' } : t
            ));
            showToast('Escrow refunded successfully', 'success');
        } catch (err) {
            showToast('Failed to refund escrow', 'error');
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const getStatusColor = (status) => {
        const colors = {
            'ACCEPTED': '#10b981',
            'REJECTED': '#ef4444',
            'PENDING': '#f59e0b',
            'UNDER_REVIEW': '#3b82f6',
            'NOT_SUBMITTED': '#6b7280'
        };
        return colors[status] || '#6b7280';
    };

    const getStatusLabel = (status) => {
        const labels = {
            'ACCEPTED': 'Approved',
            'REJECTED': 'Rejected',
            'PENDING': 'Pending',
            'UNDER_REVIEW': 'Under Review',
            'NOT_SUBMITTED': 'Not Submitted'
        };
        return labels[status] || status;
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="comp-wrapper">
            <ComplianceSidebar activeTab={activeTab} setActiveTab={setActiveTab} />

            <div className="comp-main">
                {/* Top Navigation Bar */}
                <div className="comp-topbar">
                    <div className="topbar-title">
                        <h1>Compliance Dashboard</h1>
                    </div>
                    <div className="comp-topbar-actions" style={{ display: 'flex', alignItems: 'center' }}>
                        <button
                            className={`admin-refresh-btn ${isRefreshing ? 'button-loading' : ''}`}
                            onClick={handleRefresh}
                            disabled={isRefreshing}
                        >
                            {isRefreshing ? 'Syncing...' : '🔄 Refresh Data'}
                        </button>
                        <Link to="/admin-dashboard" className="admin-dashboard-link">
                            <ArrowLeft size={18} /> Admin Dashboard
                        </Link>
                    </div>
                </div>



                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && (
                    <div className="content-area animate-in">
                        <div className="stats-grid-top">
                            <div className="stat-box" style={{ position: 'relative' }}>
                                <div className="stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}><User size={24} /></div>
                                <CardInfoTooltip title="KYC Applications" content="Total number of Know Your Customer applications pending review or currently processed." />
                                <div>
                                    <div className="stat-value">{kycQueue.length}</div>
                                    <div className="stat-label">KYC Applications</div>
                                </div>
                            </div>
                            <div className="stat-box" style={{ position: 'relative' }}>
                                <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}><DollarSign size={24} /></div>
                                <CardInfoTooltip title="Total Escrow" content="Total funds currently held in escrow pending session completion and mutual agreement." />
                                <div>
                                    <div className="stat-value">₹{(paymentStats?.escrow_total || 0).toLocaleString('en-IN')}</div>
                                    <div className="stat-label">Total Escrow</div>
                                </div>
                            </div>
                            <div className="stat-box" style={{ position: 'relative' }}>
                                <div className="stat-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}><CreditCard size={24} /></div>
                                <CardInfoTooltip title="Pending Payments" content="Payments awaiting final confirmation or payout to the experts." />
                                <div>
                                    <div className="stat-value">{escrowTransactions.filter(t => t.status === 'PENDING_CONFIRMATION').length}</div>
                                    <div className="stat-label">Pending Payments</div>
                                </div>
                            </div>
                            {/* <div className="stat-box" style={{ position: 'relative' }}>
                                <div className="stat-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}><AlertTriangle size={24} /></div>
                                <CardInfoTooltip title="Conflicts" content="Disputed sessions or financial disagreements requiring manual compliance review." />
                                <div>
                                    <div className="stat-value">{conflicts.length}</div>
                                    <div className="stat-label">Conflicts</div>
                                </div>
                            </div> */}
                        </div>

                        <div className="content-card">
                            <h2> Quick Overview</h2>
                            <div className="info-grid">
                                <div className="info-item">
                                    <strong>Pending KYC Reviews:</strong> {kycQueue.filter(k => k.status === 'UNDER_REVIEW').length}
                                </div>
                                <div className="info-item">
                                    <strong>Pending Payment Confirmations:</strong> {escrowTransactions.filter(t => t.status === 'PENDING_CONFIRMATION').length}
                                </div>
                                <div className="info-item">
                                    <strong>Escrow Holdings:</strong> ₹{(paymentStats?.total_amount_held || 0).toLocaleString('en-IN')}
                                </div>
                                <div className="info-item">
                                    <strong>Active Conflicts:</strong> {conflicts.filter(c => c.status === 'Pending').length}
                                </div>
                            </div>
                        </div>

                        {notifications.length > 0 && (
                            <div className="content-card pink-glow">
                                <h2><Bell size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> Compliance Alerts</h2>
                                <div className="notif-list-comp" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                                    {notifications.map(n => {
                                        const isSession = n.notification_type === 'SESSION_COMPLETE';
                                        const isPayment = n.notification_type === 'PAYMENT_SUCCESS' || n.notification_type === 'PAYMENT_ESCROWED';

                                        return (
                                            <div
                                                key={n.id}
                                                className="notif-item-comp"
                                                style={{
                                                    padding: '12px',
                                                    background: isSession ? '#fef2f2' : isPayment ? '#f0fdf4' : '#f8fafc',
                                                    borderRadius: '12px',
                                                    borderLeft: `5px solid ${isSession ? '#ef4444' : isPayment ? '#10b981' : '#3b82f6'}`,
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                    {isPayment && <DollarSign size={16} color="#10b981" />}
                                                    {isSession && <CheckCircle size={16} color="#ef4444" />}
                                                    <div style={{ fontWeight: 700, color: 'var(--proto-white)', fontSize: '0.95rem' }}>{n.title}</div>
                                                </div>
                                                <div style={{ fontSize: '0.9rem', color: 'var(--proto-white)', lineHeight: '1.4' }}>{n.message}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Clock size={12} />
                                                    {new Date(n.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Recently Verified Section */}
                        {approvedKYC.length > 0 && (
                            <div className="content-card">
                                <h2><CheckCircle size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> Recently Verified Professors</h2>
                                <div className="table-wrapper">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Domain</th>
                                                <th>Status</th>
                                                <th>Verified At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {approvedKYC.map(prof => (
                                                <tr key={prof.id}>
                                                    <td>
                                                        <div style={{ fontWeight: 600 }}>{prof.name}</div>
                                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{prof.email}</div>
                                                    </td>
                                                    <td>{prof.domain}</td>
                                                    <td>
                                                        <span
                                                            className="status-pill"
                                                            style={{
                                                                background: '#10b98120',
                                                                color: '#10b981',
                                                                border: '1px solid #10b981'
                                                            }}
                                                        >
                                                            Verified
                                                        </span>
                                                    </td>
                                                    <td>{new Date().toLocaleDateString('en-GB')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )
                }

                {/* KYC Queue Tab */}
                {
                    activeTab === 'kyc' && (
                        <div className="content-area animate-in">
                            <div className="content-card">
                                <div className="card-header-with-action">
                                    <div>
                                        <h2><User size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> KYC Verification Queue</h2>
                                        <p className="card-subtitle">{pendingKYC.length} total applications ({pendingKYC.filter(k => k.status !== 'ACCEPTED').length} awaiting review)</p>
                                    </div>
                                </div>

                                {pendingKYC.length === 0 ? (
                                    <div className="empty-state">
                                        <p>No pending KYC applications</p>
                                    </div>
                                ) : (
                                    <div className="kyc-grid">
                                        {pendingKYC.map(applicant => (
                                            <div key={applicant.id} className="kyc-card">
                                                <div className="kyc-card-header">
                                                    <div>
                                                        <h3>{applicant.name}</h3>
                                                        <p className="kyc-id">{applicant.id}</p>
                                                    </div>
                                                    <span
                                                        className="status-pill"
                                                        style={{
                                                            background: getStatusColor(applicant.status) + '20',
                                                            color: getStatusColor(applicant.status),
                                                            border: `1px solid ${getStatusColor(applicant.status)}`
                                                        }}
                                                    >
                                                        {getStatusLabel(applicant.status)}
                                                    </span>
                                                </div>

                                                <div className="kyc-card-info">
                                                    <div className="info-row">
                                                        <span className="info-label"><Mail size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Email:</span>
                                                        <span>{applicant.email}</span>
                                                    </div>
                                                    <div className="info-row">
                                                        <span className="info-label"><GraduationCap size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Domain:</span>
                                                        <span>{applicant.domain}</span>
                                                    </div>
                                                    <div className="info-row">
                                                        <span className="info-label"><AlertTriangle size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Risk Level:</span>
                                                        <span className={`risk-badge risk-${applicant.risk.toLowerCase()}`}>
                                                            {applicant.risk}
                                                        </span>
                                                    </div>
                                                    <div className="info-row">
                                                        <span className="info-label"><FileText size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Documents:</span>
                                                        <span>{applicant.docs.join(', ')}</span>
                                                    </div>
                                                    <div className="info-row">
                                                        <span className="info-label"><Calendar size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Submitted:</span>
                                                        <span>
                                                            {applicant.uploaded_at && !isNaN(new Date(applicant.uploaded_at).getTime())
                                                                ? new Date(applicant.uploaded_at).toLocaleDateString('en-GB')
                                                                : 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="kyc-card-actions">
                                                    <button
                                                        className="action-btn view-btn"
                                                        onClick={() => handleViewDocuments(applicant, 'kyc')}
                                                    >
                                                        <Eye size={16} style={{ marginRight: '6px' }} /> View Documents
                                                    </button>
                                                    {/* Only show action button if not already accepted */}
                                                    {applicant.status !== 'ACCEPTED' && (
                                                        <button
                                                            className="action-btn verify-btn"
                                                            onClick={() => handleOpenVerification(applicant)}
                                                        >
                                                            <CheckCircle size={16} style={{ marginRight: '6px' }} /> Take Action
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }

                {/* Escrow Tab */}
                {
                    activeTab === 'escrow' && (
                        <div className="content-area animate-in">
                            <div className="content-card">
                                <h2><DollarSign size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> Escrow Management</h2>
                                <p className="card-subtitle">Monitor and release escrowed funds</p>

                                {escrowTransactions.length === 0 ? (
                                    <div className="empty-state">
                                        <p>No escrow transactions</p>
                                    </div>
                                ) : (
                                    <div className="table-wrapper">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>Transaction</th>
                                                    <th>Professor</th>
                                                    <th>Student</th>
                                                    <th>Session Status</th>
                                                    <th>Amount</th>
                                                    <th>Escrow Status</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {escrowTransactions.map(txn => (
                                                    <tr key={`${txn.type}-${txn.id}`}>
                                                        <td style={{ fontFamily: 'monospace', color: '#6366f1' }}>#TXN-{txn.id}</td>
                                                        <td>
                                                            <div style={{ fontWeight: 600 }}>{txn.professor_name}</div>
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Domain Expert</div>
                                                        </td>
                                                        <td>{txn.student_name}</td>
                                                        <td>
                                                            <div style={{
                                                                display: 'inline-block',
                                                                padding: '4px 8px',
                                                                borderRadius: '4px',
                                                                background: (txn.is_session_completed || txn.session_status === 'COMPLETED' || txn.session_status === 'FINISHED' || txn.session_status === 'Completed') ? '#dcfce7' : 'var(--border)',
                                                                color: (txn.is_session_completed || txn.session_status === 'COMPLETED' || txn.session_status === 'FINISHED' || txn.session_status === 'Completed') ? '#166534' : 'var(--text-muted)',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600
                                                            }}>
                                                                {(txn.is_session_completed || txn.session_status === 'COMPLETED' || txn.session_status === 'FINISHED' || txn.session_status === 'Completed') ? 'Completed' : (txn.session_status || 'Pending')}
                                                            </div>
                                                        </td>
                                                        <td style={{ fontWeight: 600 }}>₹{txn.amount.toLocaleString()}</td>
                                                        <td>
                                                            <span className={`status-pill ${txn.status === 'RELEASED' ? 'completed' : txn.status === 'PENDING_CONFIRMATION' ? 'pending' : 'escrow'}`} style={{
                                                                background: txn.status === 'RELEASED' ? '#dcfce7' : txn.status === 'PENDING_CONFIRMATION' ? '#fffbeb' : '#eef2ff',
                                                                color: txn.status === 'RELEASED' ? '#15803d' : txn.status === 'PENDING_CONFIRMATION' ? '#d97706' : '#4f46e5',
                                                                border: 'none'
                                                            }}>
                                                                {txn.status === 'PENDING_CONFIRMATION' ? <><AlertTriangle size={12} style={{ marginRight: '4px', verticalAlign: 'text-bottom' }} /> Confirm Payment</> : txn.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="action-buttons">
                                                                {txn.status === 'PENDING_CONFIRMATION' && (
                                                                    <button
                                                                        className="action-btn-small release"
                                                                        style={{ padding: '6px 12px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                                        onClick={() => handleConfirmEscrow(txn)} // Passes the whole txn object (which has fields)
                                                                    >
                                                                        <CheckCircle size={12} /> Confirm
                                                                    </button>
                                                                )}

                                                                {['HELD', 'ESCROWED'].includes(txn.status) && (
                                                                    <>
                                                                        {!(txn.is_session_completed || txn.session_status === 'COMPLETED' || txn.session_status === 'FINISHED' || txn.session_status === 'Completed') && (
                                                                            <button
                                                                                className="action-btn-small"
                                                                                style={{ background: '#f59e0b', color: 'white', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                                                onClick={() => handleMarkComplete(txn)}
                                                                            >
                                                                                <CheckCircle size={12} /> Complete
                                                                            </button>
                                                                        )}
                                                                        {(txn.is_session_completed || txn.session_status === 'COMPLETED' || txn.session_status === 'FINISHED' || txn.session_status === 'Completed') && (
                                                                            <button
                                                                                className="action-btn-small release"
                                                                                onClick={() => handleReleaseEscrow(txn)}
                                                                                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                                                            >
                                                                                <Banknote size={12} /> Release
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            className="action-btn-small reject"
                                                                            onClick={() => handleDirectRefundEscrow(txn)}
                                                                            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                                                        >
                                                                            <XCircle size={12} /> Refund
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {txn.status === 'RELEASED' && (
                                                                    <span style={{ fontSize: '0.8rem', color: 'var(--text-faint)' }}>Processed</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }

                {/* Refund Management Tab */}
                {activeTab === 'refunds' && (
                    <div className="content-area animate-in">
                        <div className="content-card">
                            <div className="card-header-with-action">
                                <div>
                                    <h2><ArrowLeft size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> Refund Request Queue</h2>
                                    <p className="card-subtitle">{refundQueue.length} pending refund requests</p>
                                </div>
                            </div>

                            {refundQueue.length === 0 ? (
                                <div className="empty-state">
                                    <p>No pending refund requests</p>
                                </div>
                            ) : (
                                <div className="table-wrapper">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Request Info</th>
                                                <th>Student/Uni</th>
                                                <th>Professor</th>
                                                <th>Session Title</th>
                                                <th>Reason</th>
                                                <th>Amount</th>
                                                <th>Created At</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {refundQueue.map(req => (
                                                <tr key={req.id}>
                                                    <td>
                                                        <div style={{ fontWeight: 600 }}>#REQ-{req.id}</div>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Pay ID: #{req.payment_id}</div>
                                                    </td>
                                                    <td>{req.student_name}</td>
                                                    <td>{req.professor_name}</td>
                                                    <td>{req.session_title}</td>
                                                    <td>
                                                        <span className="status-pill pending" style={{ background: '#fee2e2', color: '#991b1b', border: 'none' }}>
                                                            {req.reason.replace(/_/g, ' ')}
                                                        </span>
                                                        {req.details && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>{req.details}</p>}
                                                    </td>
                                                    <td style={{ fontWeight: 600 }}>₹{req.amount.toLocaleString()}</td>
                                                    <td>{new Date(req.created_at).toLocaleDateString('en-GB')}</td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <button
                                                                className="action-btn-small release"
                                                                onClick={() => handleRefundRequest(req, 'APPROVED')}
                                                            >
                                                                Approve
                                                            </button>
                                                            <button
                                                                className="action-btn-small reject"
                                                                onClick={() => handleRefundRequest(req, 'REJECTED')}
                                                            >
                                                                Reject
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Pending Payments Tab */}
                {activeTab === 'pending-payments' && (
                    <div className="content-area animate-in">
                        <div className="content-card">
                            <h2><Clock size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> Pending Payments</h2>
                            <p className="card-subtitle">Review and confirm incoming payments to escrow</p>

                            {pendingPayments.length === 0 ? (
                                <div className="empty-state">
                                    <p>No valid payments waiting for confirmation</p>
                                </div>
                            ) : (
                                <div className="table-wrapper">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Transaction ID</th>
                                                <th>From (Student)</th>
                                                <th>To (Professor)</th>
                                                <th>Session</th>
                                                <th>Amount</th>
                                                <th>Payment Date</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingPayments.map(payment => (
                                                <tr key={payment.id}>
                                                    <td style={{ fontFamily: 'monospace', color: '#6366f1' }}>
                                                        {payment.transaction_id || `#PAY-${payment.id}`}
                                                    </td>
                                                    <td>{payment.student_name}</td>
                                                    <td>{payment.professor_name}</td>
                                                    <td>
                                                        <div style={{ fontWeight: 600 }}>{payment.session_title}</div>
                                                        <span className="badge-outline">{payment.session_type}</span>
                                                    </td>
                                                    <td style={{ fontWeight: 700 }}>₹{payment.amount.toLocaleString()}</td>
                                                    <td>{new Date(payment.created_at).toLocaleDateString('en-GB')}</td>
                                                    <td>
                                                        <button
                                                            className="action-btn-small release"
                                                            style={{ padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                            onClick={() => handleConfirmEscrow(payment)}
                                                        >
                                                            <CheckCircle size={14} /> Confirm Receipt
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )
                }

                {/* Conflicts Tab */}
                {
                    activeTab === 'conflicts' && (
                        <div className="content-area animate-in">
                            <div className="content-card">
                                <h2><AlertTriangle size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> Conflict Management</h2>
                                <p className="card-subtitle">Review and resolve conflicts of interest</p>

                                {conflicts.length === 0 ? (
                                    <div className="empty-state">
                                        <p>No conflicts to review</p>
                                    </div>
                                ) : (
                                    <div className="table-wrapper">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>Professor</th>
                                                    <th>Type</th>
                                                    <th>Institution</th>
                                                    <th>Severity</th>
                                                    <th>Status</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {conflicts.map(conflict => (
                                                    <tr key={conflict.id}>
                                                        <td>{conflict.professor}</td>
                                                        <td>{conflict.type}</td>
                                                        <td>{conflict.university}</td>
                                                        <td>
                                                            <span className={`severity-badge ${conflict.severity.toLowerCase()}`}>
                                                                {conflict.severity}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="status-pill pending">
                                                                {conflict.status}
                                                            </span>
                                                        </td>
                                                        <td style={{ display: 'flex', gap: '8px' }}>
                                                            <button
                                                                className="action-btn-small"
                                                                onClick={() => handleViewDocuments(conflict, 'conflict')}
                                                            >
                                                                View Details
                                                            </button>
                                                            {conflict.status === 'Pending' && (
                                                                <div className="resolution-dropdown" style={{ display: 'flex', gap: '4px' }}>
                                                                    {['Waiver', 'Donation', 'Block'].map(action => (
                                                                        <button
                                                                            key={action}
                                                                            className="action-btn-small"
                                                                            style={{ fontSize: '10px', padding: '4px 6px', background: 'var(--border)', border: '1px solid var(--border)' }}
                                                                            onClick={() => handleResolveConflict(conflict.id, action)}
                                                                        >
                                                                            {action}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }

                {/* Audit Logs Tab */}
                {
                    activeTab === 'audit' && (
                        <div className="content-area animate-in">
                            <div className="content-card">
                                <h2><ClipboardList size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> Audit Logs</h2>
                                <p className="card-subtitle">System activity and compliance actions</p>

                                <div className="table-wrapper">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Action</th>
                                                <th>User</th>
                                                <th>Timestamp</th>
                                                <th>Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {auditLogs.map(log => (
                                                <tr key={log.id}>
                                                    <td style={{ fontFamily: 'monospace' }}>#{log.id}</td>
                                                    <td><span className="status-pill completed">{log.action}</span></td>
                                                    <td>{log.user}</td>
                                                    <td>{new Date(log.timestamp).toLocaleString('en-IN')}</td>
                                                    <td style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{log.details}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Payments Tab */}
                {
                    activeTab === 'payments' && (
                        <div className="content-area animate-in">
                            <div className="content-card">
                                <div className="card-header-with-action">
                                    <div>
                                        <h2><CreditCard size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> Global Payment Ledger</h2>
                                        <p className="card-subtitle">Tracking all processed transactions across the platform</p>
                                    </div>
                                    <button className="stu-btn-express" style={{ height: 'fit-content', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Download size={16} /> Export CSV
                                    </button>
                                </div>
                                <div className="table-wrapper">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>TXN ID</th>
                                                <th>Session / Product</th>
                                                <th>From (Student)</th>
                                                <th>To (Professor)</th>
                                                <th>Amount</th>
                                                <th>Date</th>
                                                <th>Status</th>
                                                <th className="text-right"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {escrowTransactions.length > 0 ? (
                                                escrowTransactions.map(txn => (
                                                    <tr key={`txn-${txn.id}`}>
                                                        <td style={{ fontFamily: 'monospace', color: '#6366f1' }}>#TXN-{txn.id}</td>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <div style={{ fontWeight: 600 }}>{txn.session_title}</div>
                                                                {(txn.session_status === 'COMPLETED' || txn.is_session_completed || txn.session_status === 'FINISHED') ? (
                                                                    <span className="status-pill completed" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>COMPLETED</span>
                                                                ) : (
                                                                    <span className="status-pill pending" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>{txn.session_status || 'PENDING'}</span>
                                                                )}
                                                            </div>
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Scheduled: {txn.event_date ? new Date(txn.event_date).toLocaleDateString('en-GB') : 'Immediate'}</div>
                                                        </td>
                                                        <td>{txn.student_name}</td>
                                                        <td>
                                                            <div style={{ fontWeight: 500 }}>{txn.professor_name}</div>
                                                            <div className="stat-sub">Bank: Verified ✓</div>
                                                        </td>
                                                        <td style={{ fontWeight: 700 }}>₹{txn.amount}</td>
                                                        <td>{new Date().toLocaleDateString('en-GB')}</td>
                                                        <td>
                                                            <span className={`status-pill ${txn.status === 'RELEASED' ? 'completed' : 'pending'}`} style={{
                                                                background: txn.status === 'RELEASED' ? '#dcfce7' : ['HELD', 'ESCROWED'].includes(txn.status) ? '#eef2ff' : '#fffbeb',
                                                                color: txn.status === 'RELEASED' ? '#15803d' : ['HELD', 'ESCROWED'].includes(txn.status) ? '#4f46e5' : '#d97706',
                                                                border: 'none',
                                                                padding: '4px 10px',
                                                                borderRadius: '99px',
                                                                fontSize: '0.75rem'
                                                            }}>
                                                                {txn.status}
                                                            </span>
                                                        </td>
                                                        <td className="text-right">
                                                            {/* Removed redundant Confirm Escrow button for already held funds */}
                                                            {['HELD', 'ESCROWED'].includes(txn.status) && (
                                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                                    {!(txn.is_session_completed || txn.session_status === 'COMPLETED' || txn.session_status === 'FINISHED') ? (
                                                                        <button
                                                                            className="action-btn-small"
                                                                            style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                                                            onClick={() => handleMarkComplete(txn)}
                                                                        >
                                                                            Complete
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            className="action-btn-small"
                                                                            style={{ background: '#10b981', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                                                            onClick={() => handleReleaseEscrow(txn)}
                                                                        >
                                                                            Release
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {txn.status === 'RELEASED' && (
                                                                <span style={{ color: '#10b981', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    <CheckCircle size={14} /> Transaction Success
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-faint)' }}>
                                                        No payments recorded yet.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Reports Tab */}
                {
                    activeTab === 'reports' && (
                        <div className="content-area animate-in">
                            <div className="stats-grid-top">
                                <div className="stat-box" style={{ borderLeft: '4px solid #10b981' }}>
                                    <div>
                                        <div className="stat-label">Total Revenue</div>
                                        <div className="stat-value">₹{(reportsData?.summary?.total_revenue || 0).toLocaleString()}</div>
                                        <div className="stat-sub">Across All Channels</div>
                                    </div>
                                </div>
                                <div className="stat-box" style={{ borderLeft: '4px solid #f59e0b' }}>
                                    <div>
                                        <div className="stat-label">Pending Payments</div>
                                        <div className="stat-value">₹{(reportsData?.summary?.pending_payments || 0).toLocaleString()}</div>
                                        <div className="stat-sub">Awaiting Escrow Transfer</div>
                                    </div>
                                </div>
                                <div className="stat-box" style={{ borderLeft: '4px solid #3b82f6' }}>
                                    <div>
                                        <div className="stat-label">Total Sessions</div>
                                        <div className="stat-value">{reportsData?.summary?.total_sessions || 0}</div>
                                        <div className="stat-sub">Completed & Scheduled</div>
                                    </div>
                                </div>
                                <div className="stat-box" style={{ borderLeft: '4px solid #8b5cf6' }}>
                                    <div>
                                        <div className="stat-label">Student Volume</div>
                                        <div className="stat-value">{reportsData?.summary?.total_payments_count || 0}</div>
                                        <div className="stat-sub">Active Enrollments</div>
                                    </div>
                                </div>
                            </div>

                            <div className="reports-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginTop: '20px' }}>
                                <div className="content-card">
                                    <h3><BarChart size={18} style={{ marginRight: '6px', verticalAlign: 'bottom' }} /> Revenue Trends</h3>
                                    <div className="chart-placeholder" style={{ height: '200px', display: 'flex', alignItems: 'flex-end', gap: '20px', padding: '20px 0' }}>
                                        {reportsData?.monthly_data.map((m, i) => (
                                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '40px',
                                                    height: `${(m.revenue / (reportsData.summary.total_revenue || 1)) * 200 + 40}px`,
                                                    background: 'linear-gradient(to top, #3b82f6, #60a5fa)',
                                                    borderRadius: '6px 6px 0 0'
                                                }}></div>
                                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{m.month}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="content-card">
                                    <h3><Banknote size={18} style={{ marginRight: '6px', verticalAlign: 'bottom' }} /> Domain Spread</h3>
                                    <div className="domain-list" style={{ marginTop: '20px' }}>
                                        {reportsData?.domain_distribution.map((d, i) => (
                                            <div key={i} style={{ marginBottom: '15px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '0.9rem' }}>
                                                    <span>{d.name}</span>
                                                    <span style={{ fontWeight: 600 }}>{d.value}%</span>
                                                </div>
                                                <div style={{ width: '100%', height: '8px', background: 'var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                                                    <div style={{ width: `${d.value}%`, height: '100%', background: i === 0 ? '#3b82f6' : i === 1 ? '#10b981' : '#f59e0b' }}></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="content-card" style={{ marginTop: '20px' }}>
                                <h3><School size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> University Activity & Engagement</h3>
                                <div className="table-wrapper">
                                    <table className="payments-table" style={{ width: '100%', marginTop: '15px' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left' }}>Institution</th>
                                                <th style={{ textAlign: 'center' }}>Total Sessions</th>
                                                <th style={{ textAlign: 'center' }}>Active Experts</th>
                                                <th style={{ textAlign: 'center' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportsData?.uni_stats?.length > 0 ? reportsData.uni_stats.map((uni, i) => (
                                                <tr key={i}>
                                                    <td style={{ fontWeight: 500 }}>{uni.name}</td>
                                                    <td style={{ textAlign: 'center' }}>{uni.sessions}</td>
                                                    <td style={{ textAlign: 'center' }}>{uni.experts}</td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <span className="status-pill completed">Active</span>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No university data available</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="content-card" style={{ marginTop: '20px' }}>
                                <div className="card-header-with-action">
                                    <div>
                                        <h2><FileText size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> Exportable Compliance Reports</h2>
                                        <p className="card-subtitle">Download official audits for tax and regulation</p>
                                    </div>
                                </div>
                                <div className="report-download-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px', marginTop: '20px' }}>
                                    {['Annual Audit 2024', 'Escrow Holdings Q4', 'Professor Payout Ledger', 'KYC Compliance Summary'].map((title, i) => (
                                        <div key={i} className="download-item-card" style={{ padding: '15px', border: '1px solid var(--border)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{title}</span>
                                            <button className="action-btn-small" style={{ background: 'var(--border)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>PDF <Download size={12} /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )
                }

            </div >

            {/* KYC Verification Modal */}
            {
                showVerificationModal && currentApplicant && (
                    <div className="modal-overlay" onClick={() => setShowVerificationModal(false)}>
                        <div className="verification-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2><Search size={20} style={{ marginRight: '8px', verticalAlign: 'bottom' }} /> KYC Verification - {currentApplicant.name}</h2>
                                <button className="close-btn" onClick={() => setShowVerificationModal(false)}>✕</button>
                            </div>

                            <div className="modal-content">
                                <div className="applicant-info">
                                    <p><strong>ID:</strong> {currentApplicant.id}</p>
                                    <p><strong>Email:</strong> {currentApplicant.email}</p>
                                    <p><strong>Domain:</strong> {currentApplicant.domain}</p>
                                    <p><strong>Current Status:</strong> <span style={{ color: getStatusColor(currentApplicant.status) }}>{getStatusLabel(currentApplicant.status)}</span></p>
                                </div>

                                <div className="verification-notes">
                                    <label>Review Notes (Optional)</label>
                                    <textarea
                                        value={verificationNotes}
                                        onChange={(e) => setVerificationNotes(e.target.value)}
                                        placeholder="Add any notes about this verification..."
                                        rows="4"
                                    />
                                </div>

                                <div className="verification-actions">
                                    <button
                                        className="verify-action-btn accept"
                                        onClick={() => handleKYCAction('ACCEPTED')}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                    >
                                        <CheckCircle size={18} /> Accept
                                    </button>
                                    <button
                                        className="verify-action-btn pending"
                                        onClick={() => handleKYCAction('PENDING')}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                    >
                                        <Clock size={18} /> Mark Pending
                                    </button>
                                    <button
                                        className="verify-action-btn reject"
                                        onClick={() => handleKYCAction('REJECTED')}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                    >
                                        <XCircle size={18} /> Reject
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Document Viewer Modal */}
            {
                showDocViewer && selectedDocuments && (
                    <DocumentViewer
                        documents={selectedDocuments}
                        onClose={() => setShowDocViewer(false)}
                    />
                )
            }

            {/* Toast */}
            {
                toast && (
                    <div className="toast-container">
                        <div className={`toast-card ${toast.type}`}>
                            <span>{toast.type === 'success' ? <CheckCircle size={24} /> : <AlertTriangle size={24} />}</span>
                            <span>{toast.message}</span>
                        </div>
                    </div>
                )
            }

            {/* Confirmation Modal */}
            {
                confirmModal.show && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                        backdropFilter: 'blur(4px)'
                    }}>
                        <div style={{
                            background: 'var(--graphite-dark)',
                            borderRadius: '16px',
                            padding: '32px',
                            maxWidth: '500px',
                            width: '90%',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                            animation: 'modalSlideIn 0.3s ease-out'
                        }}>
                            <div style={{
                                textAlign: 'center',
                                marginBottom: '24px'
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: '#fef2f2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px',
                                    color: '#ef4444'
                                }}>
                                    <AlertTriangle size={32} />
                                </div>
                                <h2 style={{
                                    margin: '0 0 8px',
                                    fontSize: '24px',
                                    fontWeight: '700',
                                    color: '#1f2937'
                                }}>
                                    {confirmModal.type === 'kyc' && `Confirm ${confirmModal.data.actionText}?`}
                                    {confirmModal.type === 'escrow' && 'Confirm Payment'}
                                    {confirmModal.type === 'complete' && 'Mark Session Complete?'}
                                    {confirmModal.type === 'release' && 'Release Escrow Funds?'}
                                    {confirmModal.type === 'conflict' && 'Resolve Conflict?'}
                                    {confirmModal.type === 'refund_request' && `${confirmModal.data.action} Refund Request?`}
                                    {confirmModal.type === 'direct_refund' && 'Initiate Direct Refund?'}
                                </h2>
                                <p style={{
                                    margin: 0,
                                    color: '#6b7280',
                                    fontSize: '15px',
                                    lineHeight: '1.6'
                                }}>
                                    {confirmModal.type === 'kyc' && (
                                        <>
                                            {confirmModal.data.actionText} KYC for <strong>{confirmModal.data.applicant.name}</strong>
                                            {confirmModal.data.notes && (
                                                <><br /><br />Notes: <em>{confirmModal.data.notes}</em></>
                                            )}
                                        </>
                                    )}
                                    {confirmModal.type === 'escrow' && (
                                        <>
                                            Confirm payment of <strong>₹{confirmModal.data.amount}</strong> from <strong>{confirmModal.data.student_name}</strong>?
                                            <br /><br />
                                            This will move the payment to escrow (HELD status).
                                        </>
                                    )}
                                    {confirmModal.type === 'complete' && (
                                        <>
                                            Mark session <strong>#{confirmModal.data.id}</strong> as completed?
                                            <br /><br />
                                            This will allow funds to be released.
                                        </>
                                    )}
                                    {confirmModal.type === 'release' && (
                                        <>
                                            Release <strong>₹{confirmModal.data.amount}</strong> to <strong>{confirmModal.data.professor_name}</strong>?
                                            <br /><br />
                                            This will mark the payment as released and notify the professor.
                                        </>
                                    )}
                                    {confirmModal.type === 'conflict' && (
                                        <>
                                            Confirm resolution: <strong>{confirmModal.data.action}</strong>?
                                        </>
                                    )}
                                    {confirmModal.type === 'refund_request' && (
                                        <>
                                            Confirm action: <strong>{confirmModal.data.action}</strong> for refund request <strong>#REQ-{confirmModal.data.request.id}</strong>?
                                            <br /><br />
                                            Reason: {confirmModal.data.request.reason}
                                            <br />
                                            Amount: ₹{confirmModal.data.request.amount}

                                            {confirmModal.data.action === 'REJECTED' && (
                                                <div style={{ marginTop: '20px', textAlign: 'left' }}>
                                                    <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', display: 'block' }}>
                                                        Rejection Reason (Required)
                                                    </label>
                                                    <textarea
                                                        value={rejectionNotes}
                                                        onChange={(e) => setRejectionNotes(e.target.value)}
                                                        placeholder="Explain why this refund is being rejected..."
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            borderRadius: '8px',
                                                            border: '1px solid var(--border)',
                                                            minHeight: '80px',
                                                            fontSize: '14px'
                                                        }}
                                                        required
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {confirmModal.type === 'direct_refund' && (
                                        <>
                                            Directly refund <strong>₹{confirmModal.data.amount}</strong> to <strong>{confirmModal.data.student_name}</strong> from escrow?
                                            <br /><br />
                                            This will reverse the payment via Razorpay if possible.
                                        </>
                                    )}
                                </p>
                            </div>
                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'center'
                            }}>
                                <button
                                    onClick={() => setConfirmModal({ show: false, type: null, data: null })}
                                    style={{
                                        flex: 1,
                                        padding: '12px 24px',
                                        borderRadius: '8px',
                                        border: '2px solid var(--border)',
                                        background: 'var(--graphite-dark)',
                                        color: 'var(--text-muted)',
                                        fontWeight: '600',
                                        fontSize: '15px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.borderColor = 'var(--border)';
                                        e.target.style.background = '#f8fafc';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.borderColor = 'var(--border)';
                                        e.target.style.background = 'white';
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (confirmModal.type === 'kyc') confirmKYCAction();
                                        else if (confirmModal.type === 'escrow') confirmEscrowPayment();
                                        else if (confirmModal.type === 'complete') confirmMarkComplete();
                                        else if (confirmModal.type === 'release') confirmReleaseEscrow();
                                        else if (confirmModal.type === 'conflict') confirmResolveConflict();
                                        else if (confirmModal.type === 'refund_request') confirmRefundRequest();
                                        else if (confirmModal.type === 'direct_refund') confirmDirectRefund();
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '12px 24px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: '#ef4444',
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: '15px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                                    onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            <style>{`
                @keyframes modalSlideIn {
                    from {
                        transform: translateY(-50px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
            `}</style>
        </div >
    );
}

// Document Viewer Component
const DocumentViewer = ({ documents, onClose }) => {
    // MUST declare hooks at the top before any conditional returns (React rules)
    const [currentIndex, setCurrentIndex] = useState(0);


    // Handle opening KYC documents in new tab
    const handleViewKYCDocument = async (doc) => {
        try {
            // Get the token to pass as query parameter (backend expects it there for blob requests)
            const token = localStorage.getItem('token');
            const viewUrl = `/compliance/view-kyc-document/${doc.user_id}/${doc.type}?token=${token}`;

            // Fetch the document as a blob
            const response = await api.get(viewUrl, {
                responseType: 'blob'
            });

            // Create a blob URL and open in new tab
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');

            // Clean up the blob URL after a delay
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
            console.error('Error viewing document:', error);
        }
    };


    // For KYC documents, show a list of buttons
    if (documents.type === 'kyc') {
        return (
            <div className="doc-viewer-overlay" onClick={onClose}>
                <div className="doc-viewer-modal" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="doc-viewer-header">
                        <div>
                            <h2>{documents.title}</h2>
                            <p>{documents.subtitle}</p>
                        </div>
                        <button className="close-btn" onClick={onClose}>✕</button>
                    </div>

                    {/* KYC Document List */}
                    <div className="kyc-doc-list">
                        <p style={{ marginBottom: '8px', color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center' }}>
                            Click on a document to open it in a new tab for review
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {documents.documents.map((doc, index) => (
                                <button
                                    key={index}
                                    className="doc-action-btn"
                                    onClick={() => handleViewKYCDocument(doc)}
                                >
                                    <span>
                                        <FileText size={20} style={{ color: '#19D3FF' }} />
                                        {doc.name}
                                    </span>
                                    <span className="arrow"><ArrowRight size={18} /></span>
                                </button>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        );
    }


    // For other document types (conflicts, escrow), use the original iframe viewer
    const currentDoc = documents.documents[currentIndex];


    const handleNext = () => {
        if (currentIndex < documents.documents.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleDownload = () => {
        if (currentDoc) {
            window.open(currentDoc.url, '_blank');
        }
    };

    const handlePrint = () => {
        if (currentDoc) {
            const printWindow = window.open(currentDoc.url);
            printWindow.addEventListener('load', () => {
                printWindow.print();
            });
        }
    };

    return (
        <div className="doc-viewer-overlay" onClick={onClose}>
            <div className="doc-viewer-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="doc-viewer-header">
                    <div>
                        <h2>{documents.title}</h2>
                        <p>{documents.subtitle}</p>
                    </div>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                {/* Document Navigation */}
                <div className="doc-nav">
                    {documents.documents.map((doc, index) => (
                        <button
                            key={index}
                            className={`doc-tab ${index === currentIndex ? 'active' : ''}`}
                            onClick={() => setCurrentIndex(index)}
                        >
                            {doc.name || doc.type}
                        </button>
                    ))}
                </div>

                {/* Document Display */}
                <div className="doc-viewer-content">
                    {currentDoc && (
                        <>
                            <div className="doc-metadata">
                                <span><strong>Type:</strong> {currentDoc.name || currentDoc.type}</span>
                                {currentDoc.uploaded && <span><strong>Uploaded:</strong> {new Date(currentDoc.uploaded).toLocaleDateString('en-IN')}</span>}
                            </div>

                            <div className="doc-image-container">
                                <iframe
                                    src={currentDoc.url}
                                    title={currentDoc.name || currentDoc.type}
                                    className="doc-iframe"
                                />
                            </div>

                            {/* Navigation Arrows */}
                            {documents.documents.length > 1 && (
                                <div className="doc-arrows">
                                    <button
                                        className="arrow-btn"
                                        onClick={handlePrev}
                                        disabled={currentIndex === 0}
                                    >
                                        ← PREVIOUS
                                    </button>
                                    <span className="doc-counter">
                                        {currentIndex + 1} / {documents.documents.length}
                                    </span>
                                    <button
                                        className="arrow-btn"
                                        onClick={handleNext}
                                        disabled={currentIndex === documents.documents.length - 1}
                                    >
                                        NEXT →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="doc-viewer-footer">
                    <button className="doc-action-btn download" onClick={handleDownload}>
                        <Download size={16} /> DOWNLOAD
                    </button>
                    <button className="doc-action-btn print" onClick={handlePrint}>
                        <Printer size={16} /> PRINT
                    </button>
                </div>
            </div>
        </div>
    );
};

// Sidebar Component
const ComplianceSidebar = ({ activeTab, setActiveTab }) => {
    return (
        <aside className="comp-side-nav">
            <div className="side-links">
                <div className="nav-label">MAIN</div>
                <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>
                    <span className="sidebar-icon">
                        <LayoutDashboard size={18} />
                    </span>
                    Dashboard
                </button>
                <button className={activeTab === 'kyc' ? 'active' : ''} onClick={() => setActiveTab('kyc')}>
                    <span className="sidebar-icon">
                        <UserCheck size={18} />
                    </span>
                    KYC Queue
                </button>
                <button className={activeTab === 'escrow' ? 'active' : ''} onClick={() => setActiveTab('escrow')}>
                    <span className="sidebar-icon">
                        <DollarSign size={18} />
                    </span>
                    Escrow Management
                </button>
                <button className={activeTab === 'pending-payments' ? 'active' : ''} onClick={() => setActiveTab('pending-payments')}>
                    <span className="sidebar-icon">
                        <Clock size={18} />
                    </span>
                    Pending Payments
                </button>
                <button className={activeTab === 'refunds' ? 'active' : ''} onClick={() => setActiveTab('refunds')}>
                    <span className="sidebar-icon">
                        <ArrowLeft size={18} />
                    </span>
                    Refund Management
                </button>
                <button className={activeTab === 'payments' ? 'active' : ''} onClick={() => setActiveTab('payments')}>
                    <span className="sidebar-icon">
                        <CreditCard size={18} />
                    </span>
                    Payment Ledger
                </button>

                {/* <div className="nav-label">MONITORING</div>
                <button className={activeTab === 'conflicts' ? 'active' : ''} onClick={() => setActiveTab('conflicts')}>
                    <span className="sidebar-icon">
                        <AlertTriangle size={18} />
                    </span>
                    Conflicts
                </button>
                <button className={activeTab === 'audit' ? 'active' : ''} onClick={() => setActiveTab('audit')}>
                    <span className="sidebar-icon">
                        <ClipboardList size={18} />
                    </span>
                    Audit Logs
                </button>
                <button className={activeTab === 'reports' ? 'active' : ''} onClick={() => setActiveTab('reports')}>
                    <span className="sidebar-icon">
                        <BarChart size={18} />
                    </span>
                    Reports
                </button> */}
            </div>
        </aside>
    );
};

export default ComplianceDashboard;
// Forced rebuild
